import api from './api';
import { API_ROUTES } from '@/lib/constants';
import type { Account, Transaction, Budget, PaginatedResponse, ApiResponse } from '@/types/table';

export const financialService = {
  // Accounts
  getAccounts: async (): Promise<PaginatedResponse<Account>> => {
    const response = await api.get(API_ROUTES.ACCOUNTS);
    return response.data;
  },

  getAccount: async (id: string): Promise<ApiResponse<Account>> => {
    const response = await api.get(`${API_ROUTES.ACCOUNTS}/${id}`);
    return response.data;
  },

  createAccount: async (data: Partial<Account>): Promise<ApiResponse<Account>> => {
    const response = await api.post(API_ROUTES.ACCOUNTS, data);
    return response.data;
  },

  updateAccount: async (id: string, data: Partial<Account>): Promise<ApiResponse<Account>> => {
    const response = await api.patch(`${API_ROUTES.ACCOUNTS}/${id}`, data);
    return response.data;
  },

  deleteAccount: async (id: string): Promise<void> => {
    await api.delete(`${API_ROUTES.ACCOUNTS}/${id}`);
  },

  transferBetweenAccounts: async (data: { fromAccountId: string; toAccountId: string; amount: number; description?: string }): Promise<ApiResponse<{ message: string }>> => {
    const response = await api.post(`${API_ROUTES.ACCOUNTS}/transfer`, data);
    return response.data;
  },

  // Transactions
  getTransactions: async (params: Record<string, unknown>): Promise<PaginatedResponse<Transaction>> => {
    const response = await api.get(API_ROUTES.TRANSACTIONS, { params });
    return response.data;
  },

  createTransaction: async (data: Partial<Transaction>): Promise<ApiResponse<Transaction>> => {
    const response = await api.post(API_ROUTES.TRANSACTIONS, data);
    return response.data;
  },

  exportTransactions: async (params?: Record<string, unknown>): Promise<Blob> => {
    const response = await api.get(`${API_ROUTES.TRANSACTIONS}/export`, {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  // Budgets
  getBudgets: async (): Promise<PaginatedResponse<Budget>> => {
    const response = await api.get(API_ROUTES.BUDGETS);
    return response.data;
  },

  createBudget: async (data: Partial<Budget>): Promise<ApiResponse<Budget>> => {
    const response = await api.post(API_ROUTES.BUDGETS, data);
    return response.data;
  },

  updateBudget: async (id: string, data: Partial<Budget>): Promise<ApiResponse<Budget>> => {
    const response = await api.patch(`${API_ROUTES.BUDGETS}/${id}`, data);
    return response.data;
  },

  deleteBudget: async (id: string): Promise<void> => {
    await api.delete(`${API_ROUTES.BUDGETS}/${id}`);
  },

  // Reports
  getPnlReport: async (params: Record<string, unknown>): Promise<ApiResponse<Record<string, unknown>>> => {
    const response = await api.get(`${API_ROUTES.FINANCIAL_REPORTS}/pnl`, { params });
    return response.data;
  },

  getCashFlowReport: async (params: Record<string, unknown>): Promise<ApiResponse<Record<string, unknown>>> => {
    const response = await api.get(`${API_ROUTES.FINANCIAL_REPORTS}/cash-flow`, { params });
    return response.data;
  },

  getExpenseSummary: async (params: Record<string, unknown>): Promise<ApiResponse<Record<string, unknown>>> => {
    const response = await api.get(`${API_ROUTES.FINANCIAL_REPORTS}/expense-summary`, { params });
    return response.data;
  },

  getRevenueForecast: async (params: Record<string, unknown>): Promise<ApiResponse<Record<string, unknown>>> => {
    const response = await api.get(`${API_ROUTES.FINANCIAL_REPORTS}/revenue-forecast`, { params });
    return response.data;
  },

  getAgingReport: async (params: Record<string, unknown>): Promise<ApiResponse<Record<string, unknown>>> => {
    const response = await api.get(`${API_ROUTES.FINANCIAL_REPORTS}/aging`, { params });
    return response.data;
  },
};
